import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "@/components/ui/dialog";
import { 
  Form, 
  FormControl, 
  FormDescription, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from "@/components/ui/form";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { 
  Table, 
  TableBody, 
  TableCaption, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/components/ui/use-toast";
import { Calendar, Clock, Edit, Plus, Trash2, Users, Target, Calendar as CalendarIcon, MapPin } from "lucide-react";
import { format } from "date-fns";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useNavigate } from "react-router-dom";

// Types
interface Campaign {
  id: string;
  corporate_id: string;
  npo_id?: string;
  name: string;
  meta: {
    description?: string;
    goals?: string[];
    theme?: string;
    targetAudience?: string[];
    keyMetrics?: string[];
    budget?: {
      total: number;
      allocated: number;
      currency: string;
    };
    contacts?: {
      name: string;
      email: string;
      role: string;
    }[];
    relatedEvents?: string[];
    customFields?: Record<string, string>;
  };
  active_from: string;
  active_to: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface NPO {
  id: string;
  name: string;
  description: string;
  country: string;
  type: string;
  meta: Record<string, any>;
}

// API Functions
const fetchCampaigns = async (token: string, includeInactive: boolean = false): Promise<Campaign[]> => {
  const url = `${import.meta.env.VITE_CC_API_URL}/v1/campaign/corporate/campaigns${includeInactive ? '?includeInactive=true' : ''}`;
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch campaigns');
  }
  
  const data = await response.json();
  return data.data;
};

const fetchCampaignById = async (token: string, campaignId: string): Promise<Campaign> => {
  const url = `${import.meta.env.VITE_CC_API_URL}/v1/campaign/corporate/campaigns/${campaignId}`;
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch campaign');
  }
  
  const data = await response.json();
  return data.data;
};

const createCampaign = async (token: string, campaignData: Omit<Campaign, 'id' | 'corporate_id' | 'created_at' | 'updated_at'>): Promise<Campaign> => {
  const url = `${import.meta.env.VITE_CC_API_URL}/v1/campaign/corporate/campaigns`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(campaignData)
  });
  
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to create campaign');
  }
  
  const data = await response.json();
  return data.data;
};

const updateCampaign = async (token: string, campaignId: string, campaignData: Partial<Omit<Campaign, 'id' | 'corporate_id' | 'created_at' | 'updated_at'>>): Promise<Campaign> => {
  const url = `${import.meta.env.VITE_CC_API_URL}/v1/campaign/corporate/campaigns/${campaignId}`;
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(campaignData)
  });
  
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to update campaign');
  }
  
  const data = await response.json();
  return data.data;
};

const fetchNPOs = async (token: string): Promise<NPO[]> => {
  const url = `${import.meta.env.VITE_CC_API_URL}/v1/npos?limit=100`;
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch NPOs');
  }
  
  const data = await response.json();
  return data.data;
};

// Form Schema
const campaignFormSchema = z.object({
  name: z.string().min(3, { message: "Campaign name must be at least 3 characters." }),
  npo_id: z.string().optional(),
  meta: z.object({
    description: z.string().optional(),
    goals: z.array(z.string()).optional(),
    theme: z.string().optional(),
    targetAudience: z.array(z.string()).optional(),
  }).optional(),
  active_from: z.date(),
  active_to: z.date(),
  is_active: z.boolean().default(true),
});

type CampaignFormValues = z.infer<typeof campaignFormSchema>;

// Campaign Card Component
function CampaignCard({ campaign }: { campaign: Campaign }) {
  const startDate = new Date(campaign.active_from);
  const endDate = new Date(campaign.active_to);
  const isActive = campaign.is_active && new Date() >= startDate && new Date() <= endDate;
  const navigate = useNavigate();
  // Simulate volunteer progress if available (replace with actual logic if present)
  const minVolunteers = campaign.meta?.customFields?.min_volunteers
    ? Number(campaign.meta.customFields.min_volunteers)
    : 0;
  const maxVolunteers = campaign.meta?.customFields?.max_volunteers
    ? Number(campaign.meta.customFields.max_volunteers)
    : 100;
  const volunteerProgress = Math.min(100, Math.round((minVolunteers / Math.max(1, maxVolunteers)) * 100));
  // Avatar/Logo logic (fake for now, adapt as needed)
  const orgName = campaign.meta?.customFields?.org_name || campaign.npo_id || 'O';
  const orgLogoUrl = campaign.meta?.customFields?.logo_url;

  return (
    <Card
      className="overflow-hidden group cursor-pointer transition-transform hover:scale-105 hover:shadow-2xl border-0 bg-white/60 backdrop-blur-lg relative"
      style={{ boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.18)' }}
      onClick={() => navigate(`/dashboard/campaigns/${campaign.id}`)}
    >
      {/* Glassy gradient overlay */}
      <div className="absolute inset-0 pointer-events-none z-0 bg-gradient-to-br from-blue-100/60 via-white/40 to-green-100/70" />

      {/* Active/Inactive badge on top-right */}
      <div className="absolute top-4 right-4 z-10">
        {isActive ? (
          <span className="px-3 py-1 rounded-full bg-gradient-to-tr from-green-400 via-lime-400 to-blue-400 text-white text-xs font-bold shadow animate-pulse border border-white/50">
            Active
          </span>
        ) : (
          <span className="px-3 py-1 rounded-full bg-slate-300 text-slate-700 text-xs font-bold shadow border border-white/50">
            Inactive
          </span>
        )}
      </div>

      {/* Card Content */}
      <CardContent className="p-6 space-y-4 z-10 relative">
        <div className="flex items-center text-sm text-slate-600 mb-2">
          <Calendar className="h-4 w-4 mr-2" />
          <span>
            {format(startDate, "MMM d, yyyy")} - {format(endDate, "MMM d, yyyy")}
          </span>
        </div>
        <h3 className="text-xl font-semibold mb-1 text-slate-800 line-clamp-1">{campaign.name}</h3>
        <p className="text-sm text-slate-700 line-clamp-2">
          {campaign.meta.description
            ? campaign.meta.description.length > 150
              ? `${campaign.meta.description.substring(0, 150)}...`
              : campaign.meta.description
            : 'No description provided'}
        </p>
        {campaign.meta.theme && (
          <div className="flex items-center text-sm mt-1">
            <Target className="h-4 w-4 mr-2 text-blue-400" />
            <span className="font-medium text-blue-700">{campaign.meta.theme.length > 50 ? `${campaign.meta.theme.substring(0, 50)}...` : campaign.meta.theme}</span>
          </div>
        )}
        {campaign.meta.goals && campaign.meta.goals.length > 0 && (
          <div className="space-y-1 mt-2">
            <p className="text-xs font-medium text-slate-500">Goals:</p>
            <ul className="text-xs pl-4 list-disc">
              {campaign.meta.goals.map((goal, index) => (
                <li key={index}>{goal.length > 50 ? `${goal.substring(0, 50)}...` : goal}</li>
              ))}
            </ul>
          </div>
        )}
        {campaign.meta.targetAudience && campaign.meta.targetAudience.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {campaign.meta.targetAudience.map((audience, index) => (
              <Badge key={index} variant="secondary" className="bg-gradient-to-r from-blue-400 to-green-400 text-white shadow">
                {audience}
              </Badge>
            ))}
          </div>
        )}

      </CardContent>
      <CardFooter className="flex justify-end">
        <div className="text-xs text-muted-foreground">
          Created: {format(new Date(campaign.created_at), "MMM d, yyyy")}
        </div>
      </CardFooter>
    </Card>
  );
}

// Campaign Form Component
function CampaignForm({ 
  defaultValues, 
  onSubmit, 
  npos, 
  isSubmitting, 
  onCancel 
}: { 
  defaultValues?: Partial<CampaignFormValues>, 
  onSubmit: (values: CampaignFormValues) => void, 
  npos: NPO[], 
  isSubmitting: boolean,
  onCancel: () => void
}) {
  const form = useForm<CampaignFormValues>({
    resolver: zodResolver(campaignFormSchema),
    defaultValues: {
      name: defaultValues?.name || "",
      npo_id: defaultValues?.npo_id || undefined,
      meta: {
        description: defaultValues?.meta?.description || "",
        goals: defaultValues?.meta?.goals || [""],
        theme: defaultValues?.meta?.theme || "",
        targetAudience: defaultValues?.meta?.targetAudience || [""],
      },
      active_from: defaultValues?.active_from ? new Date(defaultValues.active_from) : new Date(),
      active_to: defaultValues?.active_to ? new Date(defaultValues.active_to) : new Date(new Date().setMonth(new Date().getMonth() + 3)),
      is_active: defaultValues?.is_active !== undefined ? defaultValues.is_active : true,
    },
  });
  
  const [goals, setGoals] = useState<string[]>(defaultValues?.meta?.goals || [""]);
  const [audiences, setAudiences] = useState<string[]>(defaultValues?.meta?.targetAudience || [""]);
  
  const addGoal = () => setGoals([...goals, ""]);
  const removeGoal = (index: number) => {
    const newGoals = [...goals];
    newGoals.splice(index, 1);
    setGoals(newGoals);
  };
  const updateGoal = (index: number, value: string) => {
    const newGoals = [...goals];
    newGoals[index] = value;
    setGoals(newGoals);
    form.setValue("meta.goals", newGoals.filter(g => g.trim() !== ""));
  };
  
  const addAudience = () => setAudiences([...audiences, ""]);
  const removeAudience = (index: number) => {
    const newAudiences = [...audiences];
    newAudiences.splice(index, 1);
    setAudiences(newAudiences);
  };
  const updateAudience = (index: number, value: string) => {
    const newAudiences = [...audiences];
    newAudiences[index] = value;
    setAudiences(newAudiences);
    form.setValue("meta.targetAudience", newAudiences.filter(a => a.trim() !== ""));
  };
  
  const handleSubmit = (values: CampaignFormValues) => {
    // Filter out empty goals and audiences
    values.meta = {
      ...values.meta,
      goals: goals.filter(g => g.trim() !== ""),
      targetAudience: audiences.filter(a => a.trim() !== ""),
    };
    onSubmit(values);
  };
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Campaign Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter campaign name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="npo_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Partner NPO (Optional)</FormLabel>
              <Select 
                onValueChange={field.onChange} 
                defaultValue={field.value}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an NPO partner (optional)" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {npos.map((npo) => (
                    <SelectItem key={npo.id} value={npo.id}>
                      {npo.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>
                Select an NPO partner for this campaign (optional)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="meta.description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Enter campaign description" 
                  className="min-h-[100px]" 
                  {...field} 
                  value={field.value || ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="meta.theme"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Campaign Theme</FormLabel>
              <FormControl>
                <Input placeholder="Enter campaign theme" {...field} value={field.value || ""} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div>
          <FormLabel>Campaign Goals</FormLabel>
          <div className="space-y-2 mt-2">
            {goals.map((goal, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  placeholder={`Goal ${index + 1}`}
                  value={goal}
                  onChange={(e) => updateGoal(index, e.target.value)}
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="icon" 
                  onClick={() => removeGoal(index)}
                  disabled={goals.length <= 1}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addGoal}
              className="mt-2"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Goal
            </Button>
          </div>
        </div>
        
        <div>
          <FormLabel>Target Audience</FormLabel>
          <div className="space-y-2 mt-2">
            {audiences.map((audience, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  placeholder={`Audience ${index + 1}`}
                  value={audience}
                  onChange={(e) => updateAudience(index, e.target.value)}
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="icon" 
                  onClick={() => removeAudience(index)}
                  disabled={audiences.length <= 1}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addAudience}
              className="mt-2"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Audience
            </Button>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4">
          <FormField
            control={form.control}
            name="active_from"
            render={({ field }) => (
              <FormItem className="flex flex-col flex-1">
                <FormLabel>Start Date</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="active_to"
            render={({ field }) => (
              <FormItem className="flex flex-col flex-1">
                <FormLabel>End Date</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <FormField
          control={form.control}
          name="is_active"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">
                  Active Status
                </FormLabel>
                <FormDescription>
                  Set whether this campaign is active or not
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
        
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : "Save Campaign"}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

// Main Component
export default function CorporateCampaignsPage({ session, profile }: { session: any; profile: any }) {
  const [showInactive, setShowInactive] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  // Fetch campaigns
  const { 
    data: campaigns, 
    isLoading: isLoadingCampaigns,
    error: campaignsError
  } = useQuery({
    queryKey: ['campaigns', showInactive],
    queryFn: () => fetchCampaigns(session?.access_token, showInactive),
    enabled: !!session?.access_token,
  });
  
  // Fetch NPOs for the partner selection
  const { 
    data: npos, 
    isLoading: isLoadingNPOs 
  } = useQuery({
    queryKey: ['npos'],
    queryFn: () => fetchNPOs(session?.access_token),
    enabled: !!session?.access_token,
  });
  
  // Create campaign mutation
  const createCampaignMutation = useMutation({
    mutationFn: (campaignData: Omit<Campaign, 'id' | 'corporate_id' | 'created_at' | 'updated_at'>) => 
      createCampaign(session?.access_token, campaignData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      setIsCreateDialogOpen(false);
      toast({
        title: "Campaign created",
        description: "Your campaign has been created successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  
  const handleCreateSubmit = (values: CampaignFormValues) => {
    const campaignData = {
      name: values.name,
      npo_id: values.npo_id === "none" ? null : values.npo_id,
      meta: {
        description: values.meta?.description,
        goals: values.meta?.goals,
        theme: values.meta?.theme,
        targetAudience: values.meta?.targetAudience,
      },
      active_from: values.active_from.toISOString(),
      active_to: values.active_to.toISOString(),
      is_active: values.is_active,
    };
    
    createCampaignMutation.mutate(campaignData, {
      onSuccess: () => {
        queryClient.invalidateQueries(['campaigns']);
        toast({
          title: "Campaign created",
          description: "Your campaign has been created successfully.",
        });
        setIsCreateDialogOpen(false);
      },
      onError: (error) => {
        console.error("Error creating campaign:", error);
        toast({
          title: "Error",
          description: "There was an error creating your campaign. Please try again.",
          variant: "destructive",
        });
      }
    });
  };
  
  // Filter active campaigns
  const activeCampaigns = campaigns?.filter(campaign => 
    campaign.is_active && new Date() >= new Date(campaign.active_from) && new Date() <= new Date(campaign.active_to)
  ) || [];
  
  const upcomingCampaigns = campaigns?.filter(campaign => 
    campaign.is_active && new Date() < new Date(campaign.active_from)
  ) || [];
  
  const pastCampaigns = campaigns?.filter(campaign => 
    !campaign.is_active || new Date() > new Date(campaign.active_to)
  ) || [];
  
  if (campaignsError) {
    return (
      <div className="container mx-auto py-10">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error:</strong>
          <span className="block sm:inline"> Failed to load campaigns. Please try again later.</span>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Campaigns</h1>
          <p className="text-muted-foreground mt-1">
            Create and manage your corporate social responsibility campaigns
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="show-inactive" 
              checked={showInactive} 
              onCheckedChange={(checked) => setShowInactive(checked as boolean)} 
            />
            <label 
              htmlFor="show-inactive" 
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Show inactive campaigns
            </label>
          </div>
          
          <Button onClick={() => navigate("/dashboard/campaigns/new")}>
            <Plus className="h-4 w-4 mr-2" />
            New Campaign
          </Button>
        </div>
      </div>
      
      <Tabs defaultValue="active" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="active">
            Active ({activeCampaigns.length})
          </TabsTrigger>
          <TabsTrigger value="upcoming">
            Upcoming ({upcomingCampaigns.length})
          </TabsTrigger>
          <TabsTrigger value="past">
            Past ({pastCampaigns.length})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="active" className="mt-6">
          {isLoadingCampaigns ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="w-full">
                  <CardHeader>
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-full mt-2" />
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <Skeleton className="h-4 w-1/2" />
                      <Skeleton className="h-4 w-2/3" />
                      <Skeleton className="h-4 w-1/3" />
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Skeleton className="h-9 w-20" />
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : activeCampaigns.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-muted-foreground">No active campaigns found.</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => navigate("/dashboard/campaigns/new")}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create your first campaign
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activeCampaigns.map((campaign) => (
                <CampaignCard 
                  key={campaign.id} 
                  campaign={campaign} 
                />
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="upcoming" className="mt-6">
          {isLoadingCampaigns ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2].map((i) => (
                <Card key={i} className="w-full">
                  <CardHeader>
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-full mt-2" />
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <Skeleton className="h-4 w-1/2" />
                      <Skeleton className="h-4 w-2/3" />
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Skeleton className="h-9 w-20" />
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : upcomingCampaigns.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-muted-foreground">No upcoming campaigns found.</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => navigate("/dashboard/campaigns/new")}
              >
                <Plus className="h-4 w-4 mr-2" />
                Schedule a new campaign
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcomingCampaigns.map((campaign) => (
                <CampaignCard 
                  key={campaign.id} 
                  campaign={campaign} 
                />
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="past" className="mt-6">
          {isLoadingCampaigns ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2].map((i) => (
                <Card key={i} className="w-full">
                  <CardHeader>
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-full mt-2" />
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <Skeleton className="h-4 w-1/2" />
                      <Skeleton className="h-4 w-2/3" />
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Skeleton className="h-9 w-20" />
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : pastCampaigns.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-muted-foreground">No past campaigns found.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pastCampaigns.map((campaign) => (
                <CampaignCard 
                  key={campaign.id} 
                  campaign={campaign} 
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}